import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LuRefreshCw } from 'react-icons/lu';

function ForgotPassword() {
  const apiUrl = process.env.REACT_APP_API_URL; // Get the API URL from the .env file
  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpView, setIsOtpView] = useState(false);
  const [loaded, setLoaded] = useState(false); // Button loading effect
  const focusInputRef = useRef(null); // Ref for the username input
  const navigate = useNavigate(); // Navigate to other routes after successful OTP verification
  const [email, setEmail] = useState('');
  const [captcha, setCaptcha] = useState(''); // User input for CAPTCHA
  const [generatedCaptcha, setGeneratedCaptcha] = useState(''); // Generated CAPTCHA string
  const [timer, setTimer] = useState(30); // Timer for countdown
  const [isTimerRunning, setIsTimerRunning] = useState(false); // To track if timer is active
  const [buttonDisabled, setButtonDisabled] = useState(true); // Button loading effect

  // Generate CAPTCHA on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    let interval;
    if (isTimerRunning && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsTimerRunning(false);
    }
    return () => clearInterval(interval); // Cleanup the interval on unmount
  }, [isTimerRunning, timer]);

  const generateCaptcha = () => {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const newCaptcha = Array.from({ length: 5 })
      .map(() => chars.charAt(Math.floor(Math.random() * chars.length)))
      .join('');
    setGeneratedCaptcha(newCaptcha);
  };

  const validateCaptcha = () => {
    if (captcha !== generatedCaptcha) {
      toast.error('CAPTCHA verification failed. Please try again.');
      generateCaptcha();
      setCaptcha('');
      setLoaded(false);
      return false;
    }
    return true;
  };

  const handleRefreshCaptcha = () => {
    generateCaptcha(); // Regenerate CAPTCHA
  };

  useEffect(() => {
    // Focus on the username input when the component mounts
    if (focusInputRef.current) {
      focusInputRef.current.focus();
    }
  }, []);

  const handleUsername = (e) => {
    setUsername(e.target.value);
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoaded(true);
    if (!validateCaptcha()) return;

    try {
      const data = JSON.stringify({ username });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${apiUrl}/api/auth/sendOtp`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: data,
      };

      const response = await axios.request(config);
      if (response.status === 200) {
        toast.success(response.data.message);
        setEmail(response.data.email);
        setIsOtpView(true);
        setIsTimerRunning(true); // Start the timer after sending OTP
        setTimer(30); // Reset the timer to 30 seconds
      } else {
        throw new Error(response.data.message || 'Error sending OTP');
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message);
    } finally {
      setLoaded(false);
    }
  };
  const ReSendOtp = async (e) => {
    setLoaded(true);
    try {
      const data = JSON.stringify({ username });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${apiUrl}/api/auth/sendOtp`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: data,
      };

      const response = await axios.request(config);
      if (response.status === 200) {
        toast.success(response.data.message);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message);
    } finally {
      setLoaded(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoaded(true);
    try {
      const data = JSON.stringify({ username, otp });
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${apiUrl}/api/auth/forgot-password-otpVerify`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: data,
      };

      const response = await axios.request(config);
      if (response.status === 200) {
        navigate('/resetpassword', { state: { username } }); // Redirect to reset password page
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message);
    } finally {
      setLoaded(false);
    }
  };

  const handleResendOtp = () => {
    ReSendOtp(); // Call the function to resend OTP
    setButtonDisabled(false);
  };

  return (
    <main>
      <div>
        <div className="starsec"></div>
        <div className="starthird"></div>
        <div className="starfourth"></div>
        <div className="starfifth"></div>
      </div>
      <div id="Login-page">
        <section className="section register min-vh-100 d-flex flex-column align-items-center justify-content-center py-4">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-4 col-md-6 d-flex flex-column align-items-center justify-content-center">
                <div className="d-flex justify-content-center py-4">
                  <a
                    href="index.html"
                    className="logo d-flex align-items-center w-auto"
                  >
                    <img src="assets/img/logo.png" alt="" />
                    <span className="d-none d-lg-block">PRiNTF</span>
                  </a>
                </div>
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="pt-4 pb-2">
                      <h5 className="card-title text-center pb-0 fs-4">
                        Forgot Password
                      </h5>
                      <p className="text-center small">
                        {isOtpView
                          ? 'Enter the OTP sent to your username'
                          : 'Enter your username to receive OTP'}
                      </p>
                    </div>
                    <form
                      onSubmit={isOtpView ? handleVerifyOtp : handleSendOtp}
                    >
                      <div className="row g-3">
                        <div className="col-12">
                          <label htmlFor="yourUsername" className="form-label">
                            Username
                          </label>
                          <div className="input-group has-validation">
                            <input
                              ref={focusInputRef}
                              className="form-control"
                              value={username}
                              onChange={handleUsername}
                              type="text"
                              placeholder="Enter your username"
                              required
                              disabled={isOtpView}
                            />
                            <span className="input-group-text">@</span>
                            <div className="invalid-feedback">
                              Please enter your username.
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          {!isOtpView && (
                            <>
                              <label
                                htmlFor="captchaInput"
                                className="form-label"
                              >
                                Captcha
                              </label>
                              <div className="d-flex align-items-center">
                                <span className="border border-success p-1 px-3 rounded me-3 no-select no-select">
                                  {generatedCaptcha}
                                </span>

                                <div className="input-group has-validation flex-grow-1">
                                  <input
                                    id="captchaInput"
                                    className="form-control"
                                    value={captcha}
                                    type="text"
                                    placeholder="Enter Captcha"
                                    required
                                    onChange={(e) => setCaptcha(e.target.value)}
                                  />
                                  <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={handleRefreshCaptcha}
                                  >
                                    <i className="bi bi-arrow-clockwise"></i>
                                  </button>
                                  <div className="invalid-feedback">
                                    Please enter the captcha!
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {isOtpView && (
                          <div className="col-12">
                            <label htmlFor="yourOtp" className="form-label">
                              OTP {email}
                              {/* Timer display */}
                              {isOtpView && isTimerRunning && (
                                <span className="text-success fw-bold mx-1">
                                  (0:{timer})
                                </span>
                              )}
                              {/* Resend OTP button */}
                              {!isTimerRunning &&
                                isOtpView &&
                                buttonDisabled && (
                                  <button
                                    type="button"
                                    className="btn text-success "
                                    onClick={handleResendOtp}
                                  >
                                    <LuRefreshCw className="fs-5" />
                                  </button>
                                )}
                            </label>
                            <div className="input-group has-validation">
                              <input
                                className="form-control"
                                value={otp}
                                onChange={handleOtpChange}
                                type="number"
                                required
                                onWheel={(e) => e.target.blur()}
                              />
                              <div className="invalid-feedback">
                                Please enter your OTP!
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="col-12">
                          <button
                            className="btn btn-primary w-100"
                            disabled={loaded}
                          >
                            {loaded ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>{' '}
                                Please wait...
                              </>
                            ) : isOtpView ? (
                              'Verify OTP'
                            ) : (
                              'Send OTP'
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="credits">
                  Designed by{' '}
                  <Link to="https://printftech.com/" target="_blank">
                    PRiNTF
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <ToastContainer />
    </main>
  );
}

export default ForgotPassword;
